import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalibrationTab } from '../components/CalibrationTab';
import { HistoryTab } from '../components/HistoryTab';
import { ManageTab } from '../components/ManageTab';
import { ModeTab } from '../components/ModeTab';
import { StatusTab } from '../components/StatusTab';
import { Badge, Notice, StatusDot } from '../components/ui';
import { api } from '../api/client';
import { formatAgo, formatWhen } from '../format';

const TABS = [
  { id: 'stato', label: 'Stato' },
  { id: 'modalita', label: 'Modalità' },
  { id: 'storico', label: 'Storico' },
  { id: 'calibrazione', label: 'Calibrazione' },
  { id: 'gestione', label: 'Gestione' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** Durata fissa dell'irrigazione avviata dal pulsante, cablata nel firmware. */
const BUTTON_MINUTES = 5;

export function DeviceDetail() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<TabId>('stato');
  const device = useQuery({ queryKey: ['device', id], queryFn: () => api.device(id), enabled: !!id });

  if (device.isLoading) return <p className="muted">Caricamento…</p>;
  if (device.error) return <Notice variant="error">{(device.error as Error).message}</Notice>;
  if (!device.data) return <Notice>Irrigatore non trovato.</Notice>;

  const detail = device.data;

  return (
    <div className="stack">
      <div className="row row-wrap">
        <Link to="/" className="btn btn--sm">
          ← Tutti gli irrigatori
        </Link>
      </div>

      <header className="card stack" style={{ gap: 12 }}>
        <div className="row row-wrap">
          <StatusDot online={detail.online} />
          <h2 style={{ margin: 0, fontSize: 19, letterSpacing: '-0.01em' }}>{detail.name}</h2>
          <Badge>{detail.type === 'standalone' ? 'Standalone' : `LoRa · nodo ${detail.nodeId}`}</Badge>
          {detail.irrigating && <Badge accent>Irrigazione in corso</Badge>}
          {!detail.enabled && <Badge>Sospeso</Badge>}
        </div>

        {detail.notes && (
          <p className="small secondary" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {detail.notes}
          </p>
        )}

        <div className="row row-wrap small secondary" style={{ gap: 16 }}>
          <span>Ultimo contatto: {formatAgo(detail.lastSeenAt)}</span>
          <span>
            Prossima irrigazione:{' '}
            {detail.config.mode === 'off'
              ? 'automatismi disattivati'
              : detail.nextIrrigationAt
                ? formatWhen(detail.nextIrrigationAt)
                : 'nessuna programmata'}
          </span>
          {detail.fwVersion && <span>Firmware: {detail.fwVersion}</span>}
        </div>

        {detail.type === 'lora' ? (
          <ManualControls deviceId={detail.id} />
        ) : (
          <p className="tiny muted" style={{ margin: 0 }}>
            L&apos;irrigazione manuale di questo irrigatore si avvia dal pulsante fisico sul
            controller, che apre la pompa per {BUTTON_MINUTES} minuti. Da qui si impostano solo
            le modalità automatiche.
          </p>
        )}
      </header>

      <div className="tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'stato' && <StatusTab device={detail} />}
      {tab === 'modalita' && <ModeTab device={detail} />}
      {tab === 'storico' && <HistoryTab device={detail} />}
      {tab === 'calibrazione' && <CalibrationTab device={detail} />}
      {tab === 'gestione' && <ManageTab device={detail} />}
    </div>
  );
}

/**
 * Irrigazione manuale, disponibile solo sui nodi LoRa: restano in ascolto a
 * basso consumo, quindi il gateway li raggiunge in pochi secondi. Gli
 * irrigatori standalone dormono davvero e si comandano dal pulsante fisico.
 */
function ManualControls({ deviceId }: { deviceId: string }) {
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState(5);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    queryClient.invalidateQueries({ queryKey: ['devices'] });
  };

  const irrigate = useMutation({ mutationFn: () => api.irrigate(deviceId, minutes), onSuccess: refresh });
  const stop = useMutation({ mutationFn: () => api.stop(deviceId), onSuccess: refresh });

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row row-wrap">
        <label htmlFor="manual-minutes" className="small">
          Irriga per
        </label>
        <input
          id="manual-minutes"
          type="number"
          min={1}
          max={120}
          value={minutes}
          onChange={(event) => setMinutes(Number(event.target.value))}
          style={{ width: 80 }}
        />
        <span className="small secondary">minuti</span>
        <button
          className="btn btn--primary"
          onClick={() => irrigate.mutate()}
          disabled={irrigate.isPending || minutes < 1}
        >
          Irriga ora
        </button>
        <button className="btn btn--danger" onClick={() => stop.mutate()} disabled={stop.isPending}>
          Ferma
        </button>
      </div>

      {irrigate.data && (
        <p className="tiny secondary" style={{ margin: 0 }}>
          Comando accodato: il gateway lo consegna al nodo entro pochi secondi
          {irrigate.data.estimatedAt ? ` (${formatWhen(irrigate.data.estimatedAt)})` : ''}.
        </p>
      )}
      <p className="tiny muted" style={{ margin: 0 }}>
        Il nodo resta in ascolto a basso consumo: i comandi arrivano senza attendere il risveglio.
        Se la consegna non va a buon fine il gateway riprova, e poi aspetta la prima chiamata del
        nodo; un comando che non arriva comunque viene segnato come <em>non consegnato</em> qui
        sopra, invece di sparire.
      </p>
      {(irrigate.error || stop.error) && (
        <p className="tiny" style={{ margin: 0, color: 'var(--status-critical)' }}>
          {((irrigate.error ?? stop.error) as Error).message}
        </p>
      )}
    </div>
  );
}
